import { SixDigitsCodeInputRef } from '@emerald-ui/inputs'
import { joiResolver } from '@hookform/resolvers/joi'
import LoadingButton from '@mui/lab/LoadingButton'
import { FormHelperText, FormLabel, Stack } from '@mui/material'
import { useTranslation } from 'hooks/useTranslation'
import Joi from 'joi'
import OneTimeCodeInput from 'modules/input/components/OneTimeCodeInput'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

type OtpFormProps = {
  busy: boolean
  error?: string
  clearOnSubmit?: boolean
  onSubmit: (code: string) => Promise<void>
  onChange?: () => void
  children?: ReactNode
}

export default function OtpForm({
  busy,
  error,
  clearOnSubmit = true,
  onSubmit,
  onChange,
  children
}: OtpFormProps) {
  const { t } = useTranslation()
  const input = useRef<SixDigitsCodeInputRef>(null)
  const [attempt, setAttempt] = useState(0)
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting }
  } = useForm<{ code: string }>({
    defaultValues: { code: '' },
    resolver: joiResolver(
      Joi.object({
        code: Joi.string()
          .pattern(/^\d{6}$/)
          .required()
      }).messages({
        'string.empty': t('Campo de preenchimento obrigatório'),
        'string.pattern.base': t('O código digitado não é válido')
      })
    )
  })
  const disabled = busy || isSubmitting
  useEffect(() => {
    if (!disabled) input.current?.focus()
  }, [attempt, disabled, error])
  const submit = async ({ code }: { code: string }) => {
    if (busy) return
    if (clearOnSubmit) {
      reset({ code: '' })
      setAttempt(value => value + 1)
    }
    await onSubmit(code)
  }

  return (
    <form onSubmit={handleSubmit(submit)} noValidate>
      <Stack gap="30px">
        <Controller
          control={control}
          name="code"
          render={({ field, fieldState: { error: validationError } }) => {
            const message = validationError?.message || error
            return (
              <Stack component="fieldset" sx={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                <FormLabel component="legend" error={!!message}>
                  {t('Código do autenticador')}
                </FormLabel>
                <OneTimeCodeInput
                  key={attempt}
                  label={t('Código do autenticador')}
                  ref={input}
                  disabled={disabled}
                  error={!!message}
                  onChange={(_, value) => {
                    field.onChange(value.join(''))
                    onChange?.()
                  }}
                />
                <FormHelperText error={!!message}>
                  {message || t('Digite os seis números do seu aplicativo autenticador.')}
                </FormHelperText>
              </Stack>
            )
          }}
        />
        <LoadingButton
          variant="contained"
          size="large"
          type="submit"
          loading={disabled}
          disabled={disabled || watch('code').length !== 6}
        >
          {t('Continuar')}
        </LoadingButton>
        {children}
      </Stack>
    </form>
  )
}
